import {
  useCallback, useEffect, useRef, useState,
} from 'react';

import styles from './TableRowHoverActions.module.css';
import cn from '../../../utils/cn';
import {
  useTableRowHoverDispatchOptional,
  useTableRowHoverStateOptional,
} from '../../../domain/table/useTableRowHover';
import { hasRowActions } from '../../../domain/tableRowActions/tableRowActionsUseCase';
import RenderRowActions from '../RowActionsRender/RowActionsRender';

/** Space between the table right edge and the bar, and between the bar and its row edges (px) */
const BAR_GAP = 4;

/**
 * Width kept free on the right of the table for the bar (edit + more actions pill, gaps included).
 * The table columns are sized on the remaining width.
 */
export const ROW_ACTIONS_GUTTER_WIDTH = 80;

/**
 * @param {object} props
 * @param {React.RefObject<HTMLDivElement | null>} props.containerRef - Scroll container for the table
 * @param {boolean} [props.disabled=false]
 * @param {(formUrl: string) => void} [props.onOpenFormModal]
 * @returns {JSX.Element | null}
 */
function TableRowHoverActions({
  containerRef,
  disabled = false,
  onOpenFormModal,
}) {
  const { row, element } = useTableRowHoverStateOptional();
  const { setHoveredRowLocked, clearHoveredRow } = useTableRowHoverDispatchOptional();

  const barRef = useRef(/** @type {HTMLDivElement | null} */ (null));

  const [position, setPosition] = useState(/** @type {{ top: number, left: number, height: number, hidden: boolean }} */ ({
    top: 0,
    left: 0,
    height: 0,
    hidden: true,
  }));

  const updatePosition = useCallback(() => {
    const container = containerRef.current;
    if (!container || !element || !row) {
      setPosition((prev) => (prev.hidden ? prev : {
        top: 0, left: 0, height: 0, hidden: true,
      }));
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const rowRect = element.getBoundingClientRect();
    const stickyHeader = container.querySelector('[class*="virtualized-sticky-header"]');
    const stickyBottom = stickyHeader
      ? stickyHeader.getBoundingClientRect().bottom
      : containerRect.top;

    // The scroll container (transform + contain) is the containing block for this
    // absolutely positioned bar, so offsets are expressed in content coordinates
    // (scrollTop/scrollLeft included) to stay anchored while scrolling.
    const barWidth = barRef.current?.offsetWidth ?? 0;
    const rowCenterViewport = rowRect.top + rowRect.height / 2;
    const top = container.scrollTop + (rowCenterViewport - containerRect.top);
    // Next to the row, in the gutter kept on the right of the table; a table wider than the
    // visible area puts it back on the visible right edge
    const besideRow = rowRect.right - containerRect.left + BAR_GAP;
    const left = container.scrollLeft + Math.min(besideRow, container.clientWidth - barWidth);
    const height = Math.max(0, rowRect.height - BAR_GAP);

    const hidden = rowCenterViewport < stickyBottom
      || rowRect.bottom < containerRect.top
      || rowRect.top > containerRect.bottom;

    setPosition({
      top, left, height, hidden,
    });
  }, [containerRef, element, row]);

  useEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    let rafId = 0;
    const onScrollOrResize = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        updatePosition();
        rafId = 0;
      });
    };

    container.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      container.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [containerRef, updatePosition]);

  const handleOpenStateChange = useCallback((isOpen) => {
    setHoveredRowLocked(isOpen);
  }, [setHoveredRowLocked]);

  if (!row || !hasRowActions(row.actions)) {
    return null;
  }

  return (
    <div
      ref={barRef}
      className={cn([
        styles['table-row-hover-actions'],
        position.hidden && styles['table-row-hover-actions-hidden'],
      ])}
      style={{ top: position.top, left: position.left, height: position.height }}
      onMouseLeave={(e) => {
        const toTable = e.relatedTarget instanceof Element
          && e.relatedTarget.closest('[class*="virtualized-table"]');
        if (!toTable) clearHoveredRow();
      }}
    >
      <RenderRowActions
        key={row.id}
        rowId={row.id}
        rowActions={row.actions}
        disabled={disabled}
        onOpenFormModal={onOpenFormModal}
        onOpenStateChange={handleOpenStateChange}
      />
    </div>
  );
}

export default TableRowHoverActions;
